import styled from 'styled-components';
import { FiChevronRight } from 'react-icons/fi';
import BettingIcon from '../../BettingIcon';
import { MappedIcon } from '../../FontIcon';
import dayjs from 'dayjs';
import React from 'react';
import { useBonusContext } from '../../../contexts/BonusContext';
import Ticket from './ticket.png';

type BonusType = 'boosts' | 'promotions' | 'bonus';

export const BonusItem = ({
	name,
	type,
	category,
	iconId,
	buttonTitle,
	activeBrand,
	expiredAt,
	subTitle = '',
	balance,
	onClick,
	smallSubTitle,
}: {
	name?: string;
	type: BonusType;
	category?: string;
	iconId?: number;
	buttonTitle: string;
	activeBrand: string;
	expiredAt?: string;
	subTitle?: string;
	balance?: number;
	onClick?: () => void;
	smallSubTitle?: boolean;
}) => {
	return (
		<Wrapper onClick={onClick}>
			<Row style={{ flexDirection: 'row' }}>
				<BonusIcon balance={balance} activeBrand={activeBrand} iconId={iconId!} category={category!} type={type} />
				<ContentWrapper>
					<BonusNameText>{name}</BonusNameText>
					<SubTitle smallSubTitle>{subTitle}</SubTitle>
					{expiredAt && <ExpiresText expiredAt={expiredAt} />}
				</ContentWrapper>
			</Row>
			<Row style={{ alignItems: 'center' }}>
				<ButtonText>{buttonTitle}</ButtonText>
				<FiChevronRight size={20} color="black" />
			</Row>
		</Wrapper>
	);
};
const ExpiresText = ({ expiredAt }: { expiredAt?: string }) => {
	const { tick } = useBonusContext();
	const daysDiff = React.useMemo(() => {
		const currentTime = dayjs();
		const days = dayjs(expiredAt).diff(currentTime, 'days');
		return days;
	}, [expiredAt]);
	const timeDiffString = React.useMemo(() => {
		const days = dayjs(expiredAt).diff(tick, 'days');
		if (days >= 30) {
			return `${dayjs(expiredAt).format('DD MMM YYYY')}`;
		} else if (days > 1) {
			return `${dayjs(expiredAt).diff(tick, 'days')}Days ${dayjs(expiredAt).diff(tick, 'hours') % 24}h`;
		} else {
			return `${dayjs(expiredAt).diff(tick, 'hours')}h ${dayjs(expiredAt).diff(tick, 'minutes') % 60}m ${
				dayjs(expiredAt).diff(tick, 'seconds') % 60
			}s`;
		}
	}, [expiredAt, tick]);
	return (
		<ExpiresTitle>
			Expires:
			<ExpiresLeftText daysDiff={daysDiff}>{timeDiffString}</ExpiresLeftText>
		</ExpiresTitle>
	);
};
const ExpiresLeftText = styled.span<{ daysDiff: number }>(({ theme, daysDiff }) => ({
	color: daysDiff < 1 ? '#C53030' : theme.colors.black,
	fontWeight: 'bold',
}));
const ExpiresTitle = styled.span(({ theme }) => ({
	color: theme.colors.greyR,
	fontSize: 11,
}));
const BonusIcon = ({
	type,
	category,
	iconId,
	activeBrand,
	balance,
}: {
	type: BonusType;
	category: string;
	iconId: number;
	activeBrand: string;
	balance?: number;
}) => {
	return (
		<IconWrapper>
			<TypeIcon balance={balance} type={type} />
			<IconBackground activeBrand={activeBrand}>
				{type === 'boosts' && (
					<MappedIcon
						category={category as 'sport' | 'racing'}
						typeId={iconId}
						style={{
							fontSize: 10,
						}}
						color={'black'}
					/>
				)}
				{(type === 'promotions' || type === 'bonus') && <BettingIcon icon={'playup-star'} size={10} />}
			</IconBackground>
		</IconWrapper>
	);
};
const IconBackground = styled.div<{ activeBrand: string }>(({ theme, activeBrand }) => ({
	position: 'absolute',
	bottom: 3,
	right: 3,
	backgroundColor: activeBrand === 'Fantasy' ? '#ff0056' : theme.colors.primary,
	width: 17,
	height: 17,
	borderRadius: 8.5,
	justifyContent: 'center',
	alignItems: 'center',
	borderWidth: 2,
	borderColor: theme.colors.black,
	display: 'flex',
}));
const TypeIcon = ({ type, balance }: { type: 'boosts' | 'promotions' | 'bonus'; balance?: number }) => {
	if (type === 'boosts') {
		return <BettingIcon size={25} color={'white'} icon="boost-up" />;
	}
	if (type === 'promotions') {
		return <BettingIcon size={25} color={'white'} icon="bonus-cash" />;
	}
	if (type === 'bonus') {
		return (
			<div className=" _relative">
				<TicketImg src={Ticket} />
				<div className=" _absolute _top-0 _left-0">
					<svg xmlns="http://www.w3.org/2000/svg" width="34" height="27" viewBox="0 0 34 27">
						{balance && balance > 0 && balance / 100 === 0.5 && <FiveCents />}
						{balance && balance > 0 && balance / 100 > 1 && <OverOneDollar balance={balance / 100} />}
					</svg>
				</div>
			</div>
		);
	}
	return null;
};
const FiveCents = () => {
	return (
		<g data-name="Group 1566">
			<path fill="url(#a)" d="M0 0h34v27H0z" data-name="Screen Shot 2023-03-09 at 2.09.34 pm" />
			<text
				fill="#191d22"
				fontFamily="BarlowCondensed-ExtraBoldItalic, Barlow Condensed"
				fontSize={11}
				fontStyle="italic"
				fontWeight={800}
				transform="translate(5 6.295)"
			>
				<tspan x={5.438} y={11}>
					{'50'}
				</tspan>
				<tspan y={11} fontSize={8}>
					{'c'}
				</tspan>
			</text>
		</g>
	);
};
const OverOneDollar = ({ balance }: { balance: number }) => {
	return (
		<g id="Group_1570" data-name="Group 1570" transform="translate(-7638.5 -10830.205)">
			<rect width="34" height="27" transform="translate(7638.5 10830.205)" fill="url(#pattern)" />
			<text
				transform="translate(7643 10836.5)"
				fill="#191d22"
				fontSize="6.416"
				fontFamily="BarlowCondensed-ExtraBoldItalic, Barlow Condensed"
				fontWeight="800"
				fontStyle="italic"
			>
				<tspan x="4.183" y="11" baselineShift="3.6662998971165743">
					$
				</tspan>
				<tspan y="11" fontSize="11">
					{balance}
				</tspan>
			</text>
		</g>
	);
};
const TicketImg = styled.img({
	width: 33,
	height: 27,
	position: 'relative',
});

const IconWrapper = styled.div({
	width: 40,
	height: 40,
	backgroundColor: 'black',
	paddingLeft: 4,
	paddingTop: 4,
	borderRadius: 4,
	position: 'relative',
});

const ButtonText = styled.span({
	fontSize: 9,
	textDecorationLine: 'underline',
	marginRight: 8,
	whiteSpace: 'nowrap',
});
const SubTitle = styled.span<{ smallSubTitle: boolean }>(({ smallSubTitle }) => ({
	fontSize: smallSubTitle ? 9 : 11,
	whiteSpace: 'pre-wrap',
	marginTop: 4,
	lineHeight: 1.3,
}));
const BonusNameText = styled.span({
	fontSize: 12,
	fontWeight: 'bold',
	wordBreak: 'break-word',
	maxWidth: 200,
});
const ContentWrapper = styled.div(({ theme }) => ({
	flexDirection: 'column',
	display: 'flex',
	marginLeft: 11,
}));
const Row = styled.div({
	flexDirection: 'row',
	display: 'flex',
});
const Wrapper = styled.button(({ theme }) => ({
	backgroundColor: theme.colors.white,
	padding: 10,
	borderRadius: 6,
	shadowColor: '#000',
	shadowOffset: {
		width: 0,
		height: 3,
	},
	shadowOpacity: 0.29,
	shadowRadius: 4.65,
	elevation: 7,
	flexDirection: 'row',
	justifyContent: 'space-between',
	marginBottom: 10,
	display: 'flex',
	color: theme.colors.black,
	width: '100%',
	alignItems: 'center',
	textAlign: 'left',
}));
